import { api, toCamelCase, toSnakeCase } from '../lib/api'
import {
  QuarterlyReport,
  QuarterlyReportCreate,
  QuarterlyReportDocument,
  QuarterlyReportUpdate,
} from '../types/quarterlyReport'
import { Invoice } from '../types/invoice'
import { IncomingInvoice } from '../types/incomingInvoice'

export interface GetQuarterlyReportsParams {
  userId?: number
  year?: number
  skip?: number
  limit?: number
}

function parseReport(data: Record<string, unknown>): QuarterlyReport {
  const report = toCamelCase(data) as unknown as QuarterlyReport
  report.chargeAmount = report.chargeAmount ? Number(report.chargeAmount) : null
  if (report.invoiceSummary) {
    report.invoiceSummary.outgoingTotal = Number(report.invoiceSummary.outgoingTotal) || 0
    report.invoiceSummary.incomingTotal = Number(report.invoiceSummary.incomingTotal) || 0
  }
  if (report.documents) {
    report.documents = report.documents.map((d: any) =>
      typeof d === 'object' && d !== null
        ? (toCamelCase(d as Record<string, unknown>) as unknown as QuarterlyReportDocument)
        : d
    )
  } else {
    report.documents = []
  }
  return report
}

export const quarterlyReportApi = {
  async getAll(params?: GetQuarterlyReportsParams): Promise<QuarterlyReport[]> {
    const queryParams: Record<string, string | number | undefined> = {}
    if (params?.userId) queryParams.user_id = params.userId
    if (params?.year) queryParams.year = params.year
    if (params?.skip !== undefined) queryParams.skip = params.skip
    if (params?.limit !== undefined) queryParams.limit = params.limit

    const response = await api.get('/quarterly-reports', { params: queryParams })
    return response.data.map((r: unknown) => parseReport(r as Record<string, unknown>))
  },

  async getById(id: number): Promise<QuarterlyReport> {
    const response = await api.get(`/quarterly-reports/${id}`)
    return parseReport(response.data as Record<string, unknown>)
  },

  async create(data: QuarterlyReportCreate): Promise<QuarterlyReport> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.post('/quarterly-reports', payload)
    return parseReport(response.data as Record<string, unknown>)
  },

  async update(id: number, data: QuarterlyReportUpdate): Promise<QuarterlyReport> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.put(`/quarterly-reports/${id}`, payload)
    return parseReport(response.data as Record<string, unknown>)
  },

  async finalize(id: number): Promise<QuarterlyReport> {
    const response = await api.patch(`/quarterly-reports/${id}/finalize`)
    return parseReport(response.data as Record<string, unknown>)
  },

  async getInvoices(id: number): Promise<{ outgoing: Invoice[]; incoming: IncomingInvoice[] }> {
    const response = await api.get(`/quarterly-reports/${id}/invoices`)
    return {
      outgoing: (response.data.outgoing || []).map((inv: any) => toCamelCase(inv)),
      incoming: (response.data.incoming || []).map((inv: any) => toCamelCase(inv)),
    }
  },

  async uploadDocument(reportId: number, file: File, description?: string): Promise<QuarterlyReportDocument> {
    const formData = new FormData()
    formData.append('file', file)
    if (description) formData.append('description', description)
    const response = await api.post(`/quarterly-reports/${reportId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return toCamelCase(response.data as Record<string, unknown>) as unknown as QuarterlyReportDocument
  },

  async deleteDocument(reportId: number, docId: number): Promise<void> {
    await api.delete(`/quarterly-reports/${reportId}/documents/${docId}`)
  },

  async downloadDocument(reportId: number, docId: number, filename: string): Promise<void> {
    const response = await api.get(`/quarterly-reports/${reportId}/documents/${docId}/download`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },
}
